
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Separator } from "@/components/ui/separator"
import { SleepWindowsChart } from "@/components/dashboard/SleepWindowsChart"
import { SleepDurationHistogram } from "@/components/dashboard/SleepDurationHistogram"
import { SleepRecordsTable } from "@/components/dashboard/SleepRecordsTable"
import { NapRecordsTable } from "@/components/dashboard/NapRecordsTable"
import { FeedingRecordsTable } from "@/components/dashboard/FeedingRecordsTable"
import { SleepLatencyStats } from "./SleepLatencyStats"
import { motion } from "framer-motion"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Check, Clock, Moon, Sun, Coffee, Baby } from "lucide-react"

interface ChildAnalysisCardProps {
  children: Array<{
    id: string
    name: string
  }>
  selectedChild: {
    id: string
    name: string
  }
  sleepLatency: {
    latest: number | 'N/A'
    average: number | 'N/A'
  }
  sleepRecords: any[]
  onChildChange: (childId: string) => void
  timezone?: string
}

export function ChildAnalysisCard({ 
  children, 
  selectedChild, 
  sleepLatency, 
  sleepRecords,
  onChildChange,
  timezone 
}: ChildAnalysisCardProps) {
  // Get timezone abbreviation for display
  const userTimezone = timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const timezoneAbbr = userTimezone ? 
    new Intl.DateTimeFormat('en-US', { timeZoneName: 'short', timeZone: userTimezone })
      .formatToParts(new Date())
      .find(part => part.type === 'timeZoneName')?.value || userTimezone
    : 'Local';

  return (
    <Card className="overflow-hidden border-none shadow-2xl bg-gradient-to-br from-gray-900/95 to-gray-800/95 backdrop-blur-xl hover:shadow-purple-500/10 transition-shadow duration-500">
      <CardHeader className="sticky top-0 z-10 bg-gradient-to-r from-purple-900/60 to-blue-900/60 p-6 sm:p-8 border-b border-white/5 backdrop-blur-xl">
        <div className="flex flex-col gap-4 sm:gap-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <CardTitle className="flex flex-col gap-2">
              <motion.span 
                className="text-xl sm:text-2xl md:text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-blue-400"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                Sleep Analysis Dashboard
              </motion.span>
              <p className="text-xs sm:text-sm font-normal text-gray-400 leading-relaxed">
                Professional analysis of sleep patterns, helping us optimize sleep schedules
                for better rest and development.
              </p>
            </CardTitle>
            
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Select value={selectedChild.id} onValueChange={onChildChange}>
                <SelectTrigger className="w-full sm:w-[200px] h-11 bg-purple-800/50 border-purple-500/30 hover:bg-purple-700/60 transition-all duration-300 hover:border-purple-400/50">
                  <SelectValue placeholder="Select a child" />
                </SelectTrigger>
                <SelectContent className="bg-gray-900/95 backdrop-blur-xl border-purple-500/20">
                  {children.map((child) => (
                    <SelectItem 
                      key={child.id} 
                      value={child.id}
                      className="hover:bg-purple-500/20 focus:bg-purple-500/20 cursor-pointer group transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <span className="text-white group-hover:text-purple-200 transition-colors">
                          {child.name}
                        </span>
                        {child.id === selectedChild.id && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 400, damping: 15 }}
                          >
                            <Check className="w-4 h-4 text-purple-400" />
                          </motion.div>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </motion.div>
          </div>

          <motion.div 
            className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 py-3 rounded-xl bg-purple-500/10 border border-purple-500/20 hover:bg-purple-500/15 transition-colors duration-300"
            whileHover={{ scale: 1.01 }}
          >
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-purple-400 text-sm font-medium">Currently viewing:</span>
              <span className="font-semibold text-white text-sm">{selectedChild.name}'s Analysis</span>
            </div>
            {timezone && (
              <div className="flex items-center gap-2 text-xs sm:text-sm">
                <svg className="w-4 h-4 text-purple-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-purple-300">All times in {timezoneAbbr}</span>
              </div>
            )}
          </motion.div>
        </div>
      </CardHeader>

      <CardContent className="p-4 sm:p-6 md:p-8 space-y-8 sm:space-y-12 md:space-y-16">
        {/* Top Analysis Section - Responsive Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Sleep Windows Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true, margin: "-50px" }}
            className="space-y-4 sm:space-y-6"
          >
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="p-3 rounded-full bg-purple-500/10 text-purple-400">
                <Clock className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Sleep Windows</h2>
                <p className="text-gray-400">Understanding your child's natural sleep patterns</p>
              </div>
            </div>
            
            <div className="rounded-2xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-8 shadow-xl backdrop-blur-lg border border-white/5 hover:border-purple-500/20 transition-all h-full">
              <SleepWindowsChart childId={selectedChild.id} records={sleepRecords} />
              <div className="mt-6 p-4 rounded-lg bg-purple-500/10 border border-purple-500/20">
                <p className="text-sm text-gray-300">
                  Sleep windows show when your child naturally falls asleep and wakes up. 
                  Consistent patterns indicate a well-regulated sleep schedule.
                </p>
              </div>
            </div>
          </motion.section>

          {/* Sleep Duration Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-6"
          >
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-500/10 text-blue-400">
                <Moon className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-white">Sleep Duration</h2>
                <p className="text-gray-400">How long your child sleeps each night</p>
              </div>
            </div>
            
            <div className="rounded-2xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-8 shadow-xl backdrop-blur-lg border border-white/5 hover:border-blue-500/20 transition-all h-full">
              <SleepDurationHistogram childId={selectedChild.id} records={sleepRecords} />
              <div className="mt-6 p-4 rounded-lg bg-blue-500/10 border border-blue-500/20">
                <p className="text-sm text-gray-300">
                  Sleep duration shows how many hours your child sleeps. The recommended duration varies by age, 
                  but consistency is key for healthy development.
                </p>
              </div>
            </div>
          </motion.section>
        </div>

        {/* Added proper spacing with margin-top and padding-top */}
        <div className="mt-28 pt-12">
          {/* Sleep Latency Section */}
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true, margin: "-100px" }}
            className="space-y-6"
          >
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 rounded-full bg-indigo-500/10 text-indigo-400">
                <Sun className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-semibold text-white">Sleep Latency</h2>
                <p className="text-gray-400 mt-1 max-w-xl">How quickly your child falls asleep</p>
              </div>
            </div>
            
            <div className="rounded-2xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-8 shadow-xl backdrop-blur-lg border border-white/5 hover:border-indigo-500/20 transition-all">
              <SleepLatencyStats latest={sleepLatency.latest} average={sleepLatency.average} />
              <div className="mt-8 p-4 rounded-lg bg-indigo-500/10 border border-indigo-500/20">
                <p className="text-sm text-gray-300">
                  Sleep latency measures how long it takes your child to fall asleep. Lower values indicate 
                  better sleep habits and a more effective bedtime routine.
                </p>
              </div>
            </div>
          </motion.section>
        </div>

        <Separator className="bg-white/5 my-8" />
        
        {/* Detailed Records Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true, margin: "-100px" }}
          className="space-y-6"
        >
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-green-500/10 text-green-400">
              <Baby className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Detailed Records</h2>
              <p className="text-gray-400">Complete history of sleep and feeding patterns</p>
            </div>
          </div>

          <Tabs defaultValue="sleep" className="w-full">
            <TabsList className="w-full grid grid-cols-3 gap-4 bg-gray-800/50 p-1 rounded-lg">
              <TabsTrigger 
                value="sleep" 
                className="data-[state=active]:bg-purple-600 data-[state=active]:text-white"
              >
                Sleep Records
              </TabsTrigger>
              <TabsTrigger 
                value="naps"
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white"
              >
                Nap Records
              </TabsTrigger>
              <TabsTrigger 
                value="feeding"
                className="data-[state=active]:bg-indigo-600 data-[state=active]:text-white"
              >
                Feeding Records
              </TabsTrigger>
            </TabsList>

            <TabsContent value="sleep" className="mt-6">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="rounded-2xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-6 shadow-xl backdrop-blur-lg border border-white/5"
              >
                <SleepRecordsTable childId={selectedChild.id} records={sleepRecords} timezone={userTimezone} />
              </motion.div>
            </TabsContent>

            <TabsContent value="naps" className="mt-6">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="rounded-2xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-6 shadow-xl backdrop-blur-lg border border-white/5"
              >
                <NapRecordsTable childId={selectedChild.id} timezone={userTimezone} />
              </motion.div>
            </TabsContent>

            <TabsContent value="feeding" className="mt-6">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
                className="rounded-2xl bg-gradient-to-br from-gray-800/50 to-gray-900/50 p-6 shadow-xl backdrop-blur-lg border border-white/5"
              >
                <FeedingRecordsTable childId={selectedChild.id} timezone={userTimezone} />
              </motion.div>
            </TabsContent>
          </Tabs>
        </motion.section>
      </CardContent>
    </Card>
  )
}
